import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { MiniTrendBars } from "../primitives/MiniTrendBars";
import type { UiTheme } from "../theme";
import type { Session } from "@heraion/shared";

import type { SessionSummary } from "./progressMath";
import { progressWidgetLabel } from "./progressWidgetCatalog";
import { makeProgressViewStyles } from "./progressViewStyles";
import { WidgetTitleRow } from "./InfoPill";
import { ProgressWidgetStatus } from "./ProgressWidgetStatus";
import { WeekBarsCard } from "./WeekBarsCard";

// PG-57: the Rhythm widget, the session counts and the weekly bars as one
// widget (they were two blocks at the foot of Progress). The host fetches
// the sessions. PG-64 fetta 2: Progress shows its last weeks as of today on
// the PG-63 bars (WeekBarsCard: a number on every bar, a tap reads a week);
// the report (`scope="period"`) keeps its whole period on the shared
// MiniTrendBars, the current week drawn as in progress (PG-17), with the
// period's counts.
export function ProgressRhythmCard({
  theme,
  summary,
  summaryError,
  rhythmCounts,
  formatDate,
  locale,
  scope = "now",
  partialLast = true,
  nowMs = Date.now(),
  weekStartDay,
}: {
  theme: UiTheme;
  /** The period scope reads `total` and `lastSession` only. */
  summary:
    | (Omit<SessionSummary, "lastSession"> & {
        lastSession: Pick<Session, "startedAt"> | null;
      })
    | null;
  summaryError: string | null;
  /** Calendar-week counts, oldest first, the current week last (PG-18). */
  rhythmCounts: number[];
  formatDate: (iso: string, locale: string) => string;
  locale: string;
  scope?: "now" | "period";
  /** The last bar is the week still running. A past period has none. */
  partialLast?: boolean;
  /** The instant the counts end at, to name each week. */
  nowMs?: number;
  /** The athlete's week start, the one the counts were bucketed on. */
  weekStartDay?: number;
}) {
  const { t } = useTranslation();
  const styles = makeProgressViewStyles(theme);
  // The widget's name in the panels' title voice, and the place of the ▲
  // when it is an open collapsible widget (PG-57), in every state.
  const titleRow = (
    <WidgetTitleRow theme={theme} title={progressWidgetLabel(t, "rhythm")} />
  );
  if (summaryError !== null || summary === null) {
    return (
      <ProgressWidgetStatus
        theme={theme}
        titleRow={titleRow}
        error={summaryError}
        loading={summary === null}
      />
    );
  }
  // The period's bars carry no numbers (MiniTrendBars is decorative for a
  // screen reader), so the caption under them says this week and the
  // full-week mean. Progress's bars print their numbers: the mean alone.
  const full = partialLast ? rhythmCounts.slice(0, -1) : rhythmCounts;
  const thisWeek =
    partialLast && scope === "period"
      ? t("mobile.progress.rhythmThisWeek", {
          n: rhythmCounts[rhythmCounts.length - 1] ?? 0,
          defaultValue: "This week so far: {{n}}",
        })
      : null;
  const average =
    full.length === 0
      ? null
      : // The mean names its own window (Antenore, 29/09): the weeks it is
        // computed on, not the bars a narrow card happens to show.
        t("mobile.progress.rhythmAverageWeeks", {
          count: full.length,
          avg: (
            Math.round((full.reduce((a, c) => a + c, 0) / full.length) * 10) / 10
          ).toLocaleString(locale),
          defaultValue: "Average over {{count}} full weeks: {{avg}}",
        });
  const summaryLine = [thisWeek, average].filter(Boolean).join(" · ");
  const lastSession: [string, string] = [
    t("mobile.progress.lastSession"),
    summary.lastSession ? formatDate(summary.lastSession.startedAt, locale) : "–",
  ];
  const stats: Array<[string, string]> =
    scope === "period"
      ? [
          [
            t("mobile.progress.report.sessions", {
              defaultValue: "Sessions in the period",
            }),
            String(summary.total),
          ],
          lastSession,
        ]
      : [
          [t("mobile.progress.windowAll"), String(summary.total)],
          [t("mobile.progress.window30d"), String(summary.last30d)],
          [t("mobile.progress.window7d"), String(summary.last7d)],
          lastSession,
        ];
  // A year of weeks still fits a phone: the bars thin out as they multiply.
  const bars =
    rhythmCounts.length <= 13
      ? { barWidth: 12, gap: 4 }
      : rhythmCounts.length <= 26
        ? { barWidth: 8, gap: 3 }
        : { barWidth: 3, gap: 2 };
  return (
    <View style={styles.rhythm}>
      {titleRow}
      <View style={styles.statGrid}>
        {stats.map(([label, value]) => (
          <View key={label} style={styles.stat}>
            <Text style={styles.statLabel}>{label}</Text>
            <Text style={styles.statValue}>{value}</Text>
          </View>
        ))}
      </View>
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          {t("mobile.progress.frequencyTitle")}
        </Text>
        <Text style={styles.cardHelp}>
          {scope === "period"
            ? t("mobile.progress.report.frequencyHelp", {
                defaultValue: "Sessions per week across the period.",
              })
            : // No week count: how many bars show depends on the width.
              t("mobile.progress.frequencyHelp")}
        </Text>
        {scope === "period" ? (
          <MiniTrendBars
            theme={theme}
            weeks={rhythmCounts}
            height={64}
            barWidth={bars.barWidth}
            gap={bars.gap}
            lastColor={theme.palette.brand500}
            partialLast={partialLast}
          />
        ) : (
          <WeekBarsCard
            theme={theme}
            title={t("mobile.progress.weekBars.sessions", { defaultValue: "Sessions" })}
            values={rhythmCounts}
            nowMs={nowMs}
            weekStartDay={weekStartDay}
            locale={locale}
            valueFormat={String}
            partialLast={partialLast}
          />
        )}
        {/* The caption under the bars (Antenore 28/09). */}
        {summaryLine ? <Text style={styles.cardHelp}>{summaryLine}</Text> : null}
      </View>
    </View>
  );
}
